import { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';

import { PageLoader } from '@/components/shared/loaders/PageLoader';
import { LoginPage } from '@/components/App/AppLayout/LoginPage';
import { fetchUserinfo } from '@/queries/auth/fetchUserinfo';

type AuthGatewayProps = {
  children: ReactElement;
};

/**
 * Decides logged-in vs logged-out from the Envoy session (`/auth/userinfo`).
 *
 * - loading        → page loader
 * - 401 / no session → the login page (its "Login" button navigates to /auth/signin)
 * - authenticated  → the app (a logged-in user never sees the login page)
 */
export const AuthGateway = ({
  children,
}: AuthGatewayProps) => {
  const {
    data: user,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['auth', 'userinfo'],
    queryFn: fetchUserinfo,
    retry: false,
    staleTime: Infinity,
  });

  if (isLoading) {
    return <PageLoader />;
  }

  if (isError || !user) {
    return <LoginPage />;
  }

  return children;
};
